import {
  Component,
  Inject,
  OnInit,
  OnDestroy,
  ViewChild,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject, takeUntil, finalize, switchMap, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { IdentityDocument } from '@app/core/types/client-document';
import { IdentityReportsService } from '@app/core/services/identity-reports.service';
import { NotificationService } from '@app/core/services/notification.service';
import { BrandCodes } from '@app/core/types/brand';
import {
  CreateVerificationDto,
  DOCUMENT_STATE,
  DOCUMENT_STATE_CLASS,
  IdentityVerification,
  parseDocumentState,
  VERIFICATION_FLOW,
  VERIFICATION_FLOW_LABEL,
  SlotKey,
  SLOT_KEYS,
  SLOT_CONFIGS,
  IdCardImageTypes,
  FLOW_OPTIONS,
  VerificationDecision,
  StartVerificationDialogData,
} from '@app/core/types/identity-verification';
import { MatExpansionPanel } from '@angular/material/expansion';

interface DocumentOption {
  id: number;
  name: string;
  state: DOCUMENT_STATE;
  stateClass: 'valid' | 'invalid' | 'pending' | 'validating' | 'accepted';
  reasonLabel: string;
  isFromPrev: boolean;
}

interface SlotControl {
  key: SlotKey;
  formControlName: 'frontId' | 'backId' | 'selfieId';
  label: string;
  apiType: IdCardImageTypes;
  options: DocumentOption[];
  hasOptions: boolean;
}

@Component({
  selector: 'itfg-start-identity-verification-dialog',
  templateUrl: './start-identity-verification-dialog.component.html',
  styleUrl: './start-identity-verification-dialog.component.scss',
  standalone: false,
})
export class StartVerificationDialogComponent implements OnInit, OnDestroy {
  readonly FLOW = VERIFICATION_FLOW;
  readonly FLOW_LABEL = VERIFICATION_FLOW_LABEL;
  readonly FLOW_OPTIONS = FLOW_OPTIONS;

  @ViewChild('previewTpl') previewTpl!: TemplateRef<unknown>;

  form!: FormGroup;
  slotControls: SlotControl[] = [];

  docList: IdentityDocument[] = [];
  loadingDocs = false;
  isLoading = false;

  previewUrl: string | null = null;
  previewLoading = false;
  previewError = false;
  previewDocId: number | null = null;
  previewDocName: string | null = null;

  overlayRef: OverlayRef | null = null;

  readonly unsubscribe = new Subject<void>();

  constructor(
    readonly fb: FormBuilder,
    readonly dialogRef: MatDialogRef<
      StartVerificationDialogComponent,
      IdentityVerification | null
    >,
    @Inject(MAT_DIALOG_DATA) readonly data: StartVerificationDialogData,
    readonly service: IdentityReportsService,
    readonly notificationService: NotificationService,
    readonly dialog: MatDialog,
    readonly overlay: Overlay,
    readonly viewContainerRef: ViewContainerRef,
    readonly translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.form = this.createForm();
    this.loadDocs();
  }

  loadDocs(): void {
    this.loadingDocs = true;

    this.service
      .getIdentityDocumentList$(
        this.data.client.civilId,
        this.data.client.brand.code as BrandCodes
      )
      .pipe(
        takeUntil(this.unsubscribe),
        finalize(() => (this.loadingDocs = false))
      )
      .subscribe({
        next: (docs: IdentityDocument[]) => {
          this.docList = docs;
          this.buildSlotControls();
          this.applyDefaultSelections();
        },
        error: (err: HttpErrorResponse) => {
          console.error(err);
          this.notificationService.showLocalizedErrorMessage({
            notificationText: 'verification.id.loadDocsError',
          });
        },
      });
  }

  buildSlotControls(): void {
    this.slotControls = SLOT_KEYS.map((key) => this.buildSlotControl(key));

    for (const slot of this.slotControls) {
      const ctrl = this.form.get(slot.formControlName);
      if (!slot.hasOptions) {
        ctrl?.disable({ emitEvent: false });
      } else {
        ctrl?.enable({ emitEvent: false });
      }
    }
  }

  buildSlotControl(key: SlotKey): SlotControl {
    const cfg = SLOT_CONFIGS[key];
    const prev = this.data.previousVerification;
    const prevDocId = (prev?.[cfg.dtoKey] as number | null) ?? null;
    const prevStateRaw = (prev?.[cfg.stateKey] as string | null) ?? null;

    const matchingDocs = this.docList
      .filter((d) => d.type?.name === cfg.apiType)
      .sort((a, b) => b.id - a.id);

    const options: DocumentOption[] = matchingDocs.map((doc) => {
      const isFromPrev = doc.id === prevDocId;
      const stateRaw = isFromPrev ? prevStateRaw : null;
      const parsed = parseDocumentState(stateRaw);

      return {
        id: doc.id,
        name: doc.name || `Документ #${doc.id}`,
        state: parsed.base,
        stateClass: DOCUMENT_STATE_CLASS[parsed.base],
        reasonLabel: parsed.reason
          ? this.translate.instant(`verification.id.documentActions.changeState.${parsed.reason}`)
          : '',
        isFromPrev,
      };
    });

    return {
      key,
      formControlName:
        key === 'front' ? 'frontId' : key === 'back' ? 'backId' : 'selfieId',
      label: cfg.label,
      apiType: cfg.apiType,
      options,
      hasOptions: options.length > 0,
    };
  }

  applyDefaultSelections(): void {
    const prev = this.data.previousVerification;
    if (!prev) return;

    for (const slot of this.slotControls) {
      const cfg = SLOT_CONFIGS[slot.key];
      const prevDocId = (prev[cfg.dtoKey] as number | null) ?? null;
      const prevStateRaw = (prev[cfg.stateKey] as string | null) ?? null;

      if (!prevDocId) continue;

      const parsed = parseDocumentState(prevStateRaw);
      if (parsed.base === DOCUMENT_STATE.REJECTED) continue;
      if (parsed.base === DOCUMENT_STATE.MISSING) continue;

      const optionExists = slot.options.some((o) => o.id === prevDocId);
      if (optionExists) {
        this.form.get(slot.formControlName)?.setValue(prevDocId);
      }
    }
  }

  findDocName(docId: number): string {
    const doc = this.docList.find((d) => d.id === docId);
    return doc?.name || `Документ #${docId}`;
  }

  openPreview(docId: number, event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();

    if (this.previewDocId === docId && this.overlayRef?.hasAttached()) {
      this.closePreview();
      return;
    }

    this.closePreview();

    const triggerEl = event.currentTarget as HTMLElement;

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(triggerEl)
      .withPositions([
        {
          originX: 'end',
          originY: 'center',
          overlayX: 'start',
          overlayY: 'center',
          offsetX: 12,
        },
        {
          originX: 'start',
          originY: 'center',
          overlayX: 'end',
          overlayY: 'center',
          offsetX: -12,
        },
        {
          originX: 'end',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'top',
          offsetX: 12,
        },
      ])
      .withPush(true)
      .withFlexibleDimensions(false);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      panelClass: 'svd-preview-overlay',
    });

    this.overlayRef
      .backdropClick()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => this.closePreview());

    this.previewDocId = docId;
    this.previewDocName = this.findDocName(docId);
    this.previewLoading = true;
    this.previewError = false;
    this.previewUrl = null;

    const portal = new TemplatePortal(this.previewTpl, this.viewContainerRef);
    this.overlayRef.attach(portal);

    this.service
      .renderIdentityDocumentContent(this.data.client.civilId, docId)
      .pipe(
        takeUntil(this.unsubscribe),
        finalize(() => (this.previewLoading = false))
      )
      .subscribe({
        next: (raw: Blob) => {
          const blob = new Blob([raw as any]);
          this.previewUrl = URL.createObjectURL(blob);
        },
        error: (err: HttpErrorResponse) => {
          console.error(err);
          this.previewError = true;
        },
      });
  }

  onSubmit(): void {
    const flow: VERIFICATION_FLOW = this.form.value.flow;
    const frontId = this.form.value.frontId as number | null;
    const backId = this.form.value.backId as number | null;
    const selfieId = this.form.value.selfieId as number | null;

    const documents: CreateVerificationDto['documents'] = {};
    if (frontId) documents.idFrontId = frontId;
    if (backId) documents.idBackId = backId;
    if (selfieId) documents.selfieId = selfieId;

    const dto: CreateVerificationDto = {
      civilId: this.data.client.civilId,
      brand: this.data.client.brand.code as BrandCodes,
      flow,
      documents: Object.keys(documents).length ? documents : undefined,
    };

    this.isLoading = true;

    let createdVerification: IdentityVerification;

    const activeVerification: IdentityVerification =
      this.data.activeVerification;

    const invalidateActive$ = activeVerification
      ? this.service.changeVerificationStatus$(
          activeVerification.id,
          VerificationDecision.FAIL
        )
      : of(null);

    invalidateActive$
      .pipe(
        // 1. Create new verification
        switchMap(() => this.service.createVerification(dto)),
        // 2. Set it in progress
        switchMap((verification: IdentityVerification) => {
          createdVerification = verification;
          return this.service.setVerificationInProgress(verification.id);
        }),
        takeUntil(this.unsubscribe),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: () => {
          this.notificationService.showLocalizedSuccessMessage({
            notificationText: 'verification.id.startVerificationSuccess',
          });
          this.dialogRef.close(createdVerification);
        },
        error: (err: HttpErrorResponse) => {
          console.error(err);
          this.notificationService.showLocalizedErrorMessage({
            notificationText: 'verification.id.startVerificationError',
          });
        },
      });
  }

  createForm(): FormGroup {
    return this.fb.group({
      flow: [VERIFICATION_FLOW.INTERNAL],
      frontId: [null as number | null],
      backId: [null as number | null],
      selfieId: [null as number | null],
    });
  }

  onFlowChange(flow: VERIFICATION_FLOW, panel: MatExpansionPanel): void {
    if (flow !== VERIFICATION_FLOW.INTERNAL) {
      this.form.patchValue(
        { frontId: null, backId: null, selfieId: null },
        { emitEvent: false }
      );
      panel.close();
      this.closePreview();
    }
  }

  closePreview(): void {
    if (this.overlayRef) {
      this.overlayRef.detach();
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
    this.previewUrl = null;
    this.previewDocId = null;
    this.previewDocName = null;
    this.previewError = false;
    this.previewLoading = false;
  }

  clearSlotSelection(slot: SlotControl, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.form.get(slot.formControlName)?.setValue(null);
  }

  onNoClick(): void {
    this.dialogRef.close(null);
  }

  ngOnDestroy(): void {
    this.closePreview();
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
